/* @flow */
import React from 'react';
import SimpleImageCarousel from '@homeaway/react-simple-image-carousel';
import {SVGIcon} from '@homeaway/react-svg';
import PaginationDots from '@homeaway/react-pagination-dots';
import {CHEVRON_LEFT__16, CHEVRON_RIGHT__16} from '@homeaway/svg-defs';
import HitLabel from '../../HitLabel/HitLabel';
import {convertImagesIntoSimpleCarouselFormat} from '../../HitCarouselContainer/HitCarouselContainer';
import {
    PHOTOCAROUSEL_LEFT_LABEL,
    PHOTOCAROUSEL_RIGHT_LABEL,
    triggerCarouselEDAPEvent,
    triggerCarouselImagePositionEdapEvent
} from '../../../utils/carouselEdapUtils';

import './HitExperimentCarousel.less';

import type {BadgeShape, ListingImage} from '../../Hit/Hit';

type Props = {
    detailPageUrl: string,
    edapAction: string,
    images: ListingImage[],
    onImageClick?: Function,
    premierPartnerBadge?: BadgeShape
};

const HitExperimentCarousel = (props: Props) => {
    const {
        detailPageUrl,
        edapAction,
        images,
        onImageClick,
        premierPartnerBadge
    } = props;
    const [imageIndex, setImageIndex] = React.useState(0);
    const carouselImages = convertImagesIntoSimpleCarouselFormat(images);

    const onImageNavigate = (index: number) => {
        const previousIndex = imageIndex;
        const imageSrc = carouselImages[index].src;

        setImageIndex(index);

        const edapLabel =
            (previousIndex + 1 === carouselImages.length && index === 0) ||
            previousIndex + 1 === index
                ? PHOTOCAROUSEL_RIGHT_LABEL
                : PHOTOCAROUSEL_LEFT_LABEL;

        triggerCarouselEDAPEvent(edapAction, edapLabel);

        if (imageSrc) {
            triggerCarouselImagePositionEdapEvent(`${imageSrc}:${index}`);
        }
    };

    return (
        <div className="HitExperimentCarousel">
            {premierPartnerBadge && (
                <div className={'HitExperimentCarousel__badge'}>
                    <HitLabel label={premierPartnerBadge.name} />
                </div>
            )}
            <SimpleImageCarousel
                images={carouselImages}
                index={imageIndex}
                href={detailPageUrl}
                target={'_blank'}
                transitionStyle="slide"
                transitionDuration={250}
                onNextClick={onImageNavigate}
                onPrevClick={onImageNavigate}
                onClick={onImageClick}
                tabIndex={-1}
                renderButtons={({setPrevRef, setNextRef}) => {
                    return [
                        <button
                            key="0"
                            className="SimpleImageCarousel__button SimpleImageCarousel__button--left"
                            data-wdio="SimpleImageCarousel__button--prev"
                            ref={setPrevRef}
                            tabIndex="-1"
                        >
                            <span className="sr-only">{'Previous Image'}</span>
                            <SVGIcon
                                className="SimpleImageCarousel__svg SimpleImageCarousel__svg--left"
                                markup={CHEVRON_LEFT__16}
                            />
                        </button>,
                        <button
                            key="1"
                            className="SimpleImageCarousel__button SimpleImageCarousel__button--right"
                            data-wdio="SimpleImageCarousel__button--next"
                            ref={setNextRef}
                            tabIndex="-1"
                        >
                            <span className="sr-only">{'Next Image'}</span>
                            <SVGIcon
                                className="SimpleImageCarousel__svg SimpleImageCarousel__svg--right"
                                markup={CHEVRON_RIGHT__16}
                            />
                        </button>
                    ];
                }}
            />
            <PaginationDots
                imageIndex={imageIndex}
                maxImages={carouselImages.length}
            />
        </div>
    );
};

export default HitExperimentCarousel;
